/**
 * Common SVG props
 */
const svgProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '1.5',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * Icon Components as SVGs
 */
export const UserIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const FolderOpenIcon = ({
  size = 20,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg {...svgProps} width={size} height={size} stroke={color}>
    <path d="m6 14 1.45-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.55 6a2 2 0 0 1-1.94 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H18a2 2 0 0 1 2 2v2" />
  </svg>
);

export const PlayIcon = ({
  size = 20,
  fill = 'none',
}: {
  size?: number;
  fill?: string;
}) => (
  <svg {...svgProps} width={size} height={size} fill={fill}>
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

export const AlertTriangleIcon = ({
  size = 20,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg {...svgProps} width={size} height={size} stroke={color}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

export const SearchIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const GiftIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8" />
    <path d="M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
  </svg>
);

export const InfoIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

export const FlameIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

export const MegaphoneIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

export const SendIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </svg>
);

export const CheckIcon = ({
  size = 16,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg {...svgProps} width={size} height={size} stroke={color}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const CheckCheckIcon = ({
  size = 16,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg {...svgProps} width={size} height={size} stroke={color}>
    <path d="M18 6 7 17l-5-5" />
    <path d="m22 10-7.5 7.5L13 16" />
  </svg>
);

/**
 * Get file icon based on MIME type (returns SVG element)
 */
export const getFileIcon = (mimeType: string): JSX.Element => {
  const customSvgProps = {
    ...svgProps,
    width: 20,
    height: 20,
    stroke: '#1f2937',
  };

  if (mimeType.startsWith('image/')) {
    return (
      <svg {...customSvgProps}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    );
  }

  if (mimeType.startsWith('video/')) {
    return (
      <svg {...customSvgProps}>
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
    );
  }

  if (mimeType.startsWith('audio/')) {
    return (
      <svg {...customSvgProps}>
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    );
  }

  if (mimeType.includes('pdf')) {
    return (
      <svg {...customSvgProps}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    );
  }

  if (mimeType.includes('word') || mimeType.includes('document')) {
    return (
      <svg {...customSvgProps}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="12" y1="9" x2="8" y2="9" />
      </svg>
    );
  }

  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return (
      <svg {...customSvgProps}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M8 13h8" />
        <path d="M8 17h8" />
        <path d="M10 9H8" />
      </svg>
    );
  }

  if (mimeType.includes('zip') || mimeType.includes('rar')) {
    return (
      <svg {...customSvgProps}>
        <path d="M10 2v4" />
        <path d="M14 2v4" />
        <rect x="4" y="6" width="16" height="14" rx="2" />
        <path d="M12 10v4" />
      </svg>
    );
  }

  // Default: paperclip
  return (
    <svg {...customSvgProps}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
};

/**
 * Format file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/**
 * Format date for separator
 */
export const formatDateSeparator = (date: Date | string): string => {
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return ` ${day}/${month}/${year}`;
};

/**
 * Check if two dates are on different days
 */
export const isDifferentDay = (
  date1: Date | string,
  date2: Date | string,
): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getDate() !== d2.getDate() ||
    d1.getMonth() !== d2.getMonth() ||
    d1.getFullYear() !== d2.getFullYear()
  );
};

/**
 * Format last seen time
 */
export const formatLastSeen = (lastSeen?: Date) => {
  if (!lastSeen) return '';
  const now = new Date();
  const diff = now.getTime() - lastSeen.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

/**
 * Get reaction image path from reaction number
 */
export const getReactionImage = (reactionNum: number): string => {
  const reactions: { [key: number]: string } = {
    1: '/Chatbox/reaction/heart.png',
    2: '/Chatbox/reaction/laughing.png',
    3: '/Chatbox/reaction/cry.png',
    4: '/Chatbox/reaction/like.png',
    5: '/Chatbox/reaction/angry.png',
    6: '/Chatbox/reaction/suprise.png',
  };
  return reactions[reactionNum] || '';
};

export const CameraIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

export const VideoIcon = ({ size = 20 }: { size?: number }) => (
  <svg {...svgProps} width={size} height={size}>
    <path d="m22 8-6 4 6 4V8Z" />
    <rect x="2" y="6" width="14" height="12" rx="2" ry="2" />
  </svg>
);
